/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

/**
 * API Client for AI Expert System Management
 * 
 * Centralized API service using axios for all backend communications.
 * Can be easily moved to another repository without changes.
 */

import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { authService } from './auth';
import type {
  LlmProvider,
  LlmProviderCreate,
  LlmProviderFilters,
  EmbeddingModel,
  EmbeddingModelFilters,
  LlmModel,
  LlmModelCreate,
  LlmModelFilters,
  AISystem,
  AISystemCreate,
  AISystemFilters,
  AISystemListItem,
  Expert,
  ExpertCreate,
  ExpertFilters,
  ExpertListItem,
  AISchedule,
  AIScheduleCreate,
  AIScheduleFilters,
  AISystemExecution,
  AISystemExecutionListItem,
  ExecutionFilters,
  ExpertExecutionResult,
  AISystemExecuteRequest,
  PaginatedResponse,
  ErrorResponse,
  AIBacktestConfig,
  AIBacktestConfigCreate,
  AIBacktestConfigFilters,
  AIBacktestRun,
  AIBacktestRunDetail,
  AIBacktestRunFilters,
  AIBacktestTrade,
  AISystemTradesResponse,
  AISystemAnalyticsResponse,
  ModelLabDataset,
  ModelLabDatasetCreate,
  ModelLabDatasetFilters,
  ModelLabDatasetVersion,
  ModelLabDatasetVersionCreate,
  ModelLabDatasetVersionFilters,
  ModelLabAsset,
  ModelLabAssetCreate,
  ModelLabAssetFilters,
  ModelLabVersion,
  ModelLabVersionFilters,
  ModelLabTrainingJob,
  ModelLabTrainingJobCreate,
  ModelLabTrainingJobFilters,
  ModelLabDeployment,
  ModelLabDeploymentCreate,
  ModelLabDeploymentFilters,
  ModelLabFeatureSet,
  ModelLabFeatureSetCreate,
  ModelLabFeatureSetFilters,
  DataProfile,
  FeaturePreview,
  ExperimentComparison,
  ModelLineage,
  ModelInternals,
  DeploymentHealth,
  PredictionResult,
  ModelLabHPTuningJob,
  ModelLabHPTuningJobCreate,
  ModelLabHPTuningJobFilters,
  ModelLabAutoMLRun,
  ModelLabAutoMLRunCreate,
  ModelLabAutoMLRunFilters,
  ModelLabExplainabilityReport,
  ModelLabExplainabilityReportCreate,
  ModelLabModelMonitor,
  ModelLabModelMonitorCreate,
  ModelLabDriftReport,
  ModelLabPredictionLog,
  ModelLabRLTrainingJob,
  ModelLabRLTrainingJobCreate,
  ComputeSpec,
  ComputeSpecCreate,
  ComputeVM,
  ComputeSuggestion,
} from '../types';

// API Configuration — use the same pattern as the main frontend: host from env + /api suffix
const API_BASE_URL = `${import.meta.env.VITE_API_BASE_URL || ''}/api`;
const API_TIMEOUT = 30000; // 30 seconds for normal operations
const EXECUTION_TIMEOUT = 900000; // 15 minutes for expert/system execution (increased for reasoning models)

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Track if token refresh is in progress
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any = null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// Request interceptor (add auth token, attempt refresh if expired)
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const token = authService.getAccessToken();

    if (token) {
      // Check if token is expired or about to expire
      if (authService.isTokenExpired(token) && authService.getRefreshToken()) {
        if (!isRefreshing) {
          isRefreshing = true;

          try {
            const newToken = await authService.refreshAccessToken();
            isRefreshing = false;
            processQueue(null, newToken);
            config.headers.Authorization = `Bearer ${newToken}`;
          } catch (error) {
            isRefreshing = false;
            processQueue(error, null);
            // Still send the current token — let the server decide
            config.headers.Authorization = `Bearer ${token}`;
          }
        } else {
          // Wait for token refresh to complete
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((newToken) => {
              config.headers.Authorization = `Bearer ${newToken}`;
              return config;
            })
            .catch(() => {
              // Refresh failed, use current token
              config.headers.Authorization = `Bearer ${token}`;
              return config;
            });
        }
      } else {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor (handle 401 errors and retry with refreshed token)
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ErrorResponse>) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Handle 401 Unauthorized — only attempt refresh if a refresh token exists
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      authService.getRefreshToken()
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return apiClient(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const newToken = await authService.refreshAccessToken();
        isRefreshing = false;
        processQueue(null, newToken);

        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        isRefreshing = false;
        processQueue(refreshError, null);
        // Don't logout or hard-redirect — let the component handle it
        return Promise.reject(refreshError);
      }
    }

    // Re-throw error for component-level handling
    return Promise.reject(error);
  }
);

// Helper function to build query string from filters
function buildQueryString(params?: Record<string, any>): string {
  if (!params) return '';
  
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.append(key, String(value));
    }
  });
  
  const query = searchParams.toString();
  return query ? `?${query}` : '';
}

// =======================
// LLM Provider API
// =======================

export const llmProviderApi = {
  list: async (filters?: LlmProviderFilters): Promise<PaginatedResponse<LlmProvider>> => {
    const response = await apiClient.get(`/ai/expert/api/providers/${buildQueryString(filters)}`);
    return response.data;
  },

  get: async (id: number): Promise<LlmProvider> => {
    const response = await apiClient.get(`/ai/expert/api/providers/${id}/`);
    return response.data;
  },

  create: async (data: LlmProviderCreate): Promise<LlmProvider> => {
    const response = await apiClient.post('/ai/expert/api/providers/', data);
    return response.data;
  },

  update: async (id: number, data: Partial<LlmProviderCreate>): Promise<LlmProvider> => {
    const response = await apiClient.patch(`/ai/expert/api/providers/${id}/`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/ai/expert/api/providers/${id}/`);
  },

  getModels: async (id: number): Promise<LlmModel[]> => {
    const response = await apiClient.get(`/ai/expert/api/providers/${id}/models/`);
    return response.data;
  },
};

// =======================
// LLM Model API
// =======================

export const llmModelApi = {
  list: async (filters?: LlmModelFilters): Promise<PaginatedResponse<LlmModel>> => {
    const response = await apiClient.get(`/ai/expert/api/models/${buildQueryString(filters)}`);
    return response.data;
  },

  get: async (id: number): Promise<LlmModel> => {
    const response = await apiClient.get(`/ai/expert/api/models/${id}/`);
    return response.data;
  },

  create: async (data: LlmModelCreate): Promise<LlmModel> => {
    const response = await apiClient.post('/ai/expert/api/models/', data);
    return response.data;
  },

  update: async (id: number, data: Partial<LlmModelCreate>): Promise<LlmModel> => {
    const response = await apiClient.patch(`/ai/expert/api/models/${id}/`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/ai/expert/api/models/${id}/`);
  },
};

export const embeddingModelApi = {
  list: async (filters?: EmbeddingModelFilters): Promise<PaginatedResponse<EmbeddingModel>> => {
    const response = await apiClient.get(`/ai/expert/api/embedding-models/${buildQueryString(filters)}`);
    return response.data;
  },

  get: async (id: number): Promise<EmbeddingModel> => {
    const response = await apiClient.get(`/ai/expert/api/embedding-models/${id}/`);
    return response.data;
  },

  create: async (data: EmbeddingModelCreate): Promise<EmbeddingModel> => {
    const response = await apiClient.post('/ai/expert/api/embedding-models/', data);
    return response.data;
  },

  update: async (id: number, data: Partial<EmbeddingModelCreate>): Promise<EmbeddingModel> => {
    const response = await apiClient.patch(`/ai/expert/api/embedding-models/${id}/`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/ai/expert/api/embedding-models/${id}/`);
  },
};

// =======================
// AI System API
// =======================

export const aiSystemApi = {
  list: async (filters?: AISystemFilters): Promise<PaginatedResponse<AISystemListItem>> => {
    const response = await apiClient.get(`/ai/expert/api/systems/${buildQueryString(filters)}`);
    return response.data;
  },

  get: async (id: number): Promise<AISystem> => {
    const response = await apiClient.get(`/ai/expert/api/systems/${id}/`);
    return response.data;
  },

  create: async (data: AISystemCreate): Promise<AISystem> => {
    const response = await apiClient.post('/ai/expert/api/systems/', data);
    return response.data;
  },

  update: async (id: number, data: Partial<AISystemCreate>): Promise<AISystem> => {
    const response = await apiClient.patch(`/ai/expert/api/systems/${id}/`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/ai/expert/api/systems/${id}/`);
  },

  execute: async (id: number, data: AISystemExecuteRequest): Promise<AISystemExecution> => {
    const response = await apiClient.post(
      `/ai/expert/api/systems/${id}/execute/`,
      data,
      { timeout: EXECUTION_TIMEOUT } // Use longer timeout for execution
    );
    return response.data;
  },

  getExperts: async (id: number): Promise<ExpertListItem[]> => {
    const response = await apiClient.get(`/ai/expert/api/systems/${id}/experts/`);
    return response.data;
  },

  getExecutions: async (id: number): Promise<AISystemExecutionListItem[]> => {
    const response = await apiClient.get(`/ai/expert/api/systems/${id}/executions/`);
    return response.data;
  },

  /**
   * Export an AI system to JSON format with all dependencies
   * @param id - System ID to export
   * @param maskApiKeys - Whether to mask API keys (default: true)
   * @param indent - JSON indentation level (default: 2)
   */
  export: async (
    id: number,
    options?: { mask_api_keys?: boolean; indent?: number }
  ): Promise<Record<string, any>> => {
    const params = {
      mask_api_keys: options?.mask_api_keys ?? true,
      indent: options?.indent ?? 2,
    };
    const response = await apiClient.get(
      `/ai/expert/api/systems/${id}/export/${buildQueryString(params)}`
    );
    return response.data;
  },

  /**
   * Import an AI system from JSON export data
   * @param data - Export data from export() method
   * @param apiKeyMapping - Optional mapping of provider export IDs to real API keys
   */
  importSystem: async (
    data: Record<string, any>,
    apiKeyMapping?: Record<string, string>
  ): Promise<{ success: boolean; message: string; ai_system: AISystem }> => {
    const response = await apiClient.post('/ai/expert/api/systems/import_system/', {
      data,
      api_key_mapping: apiKeyMapping || {},
    });
    return response.data;
  },

  /**
   * Clone an AI system with all its experts and connections
   * @param id - System ID to clone
   * @param name - Optional custom name for the cloned system
   */
  clone: async (
    id: number,
    name?: string
  ): Promise<{ success: boolean; message: string; ai_system: AISystem }> => {
    const response = await apiClient.post(`/ai/expert/api/systems/${id}/clone/`, {
      name,
    });
    return response.data;
  },

  /**
   * Get trades created by this AI system
   * @param id - System ID
   * @param options - Filter options (status, limit, offset)
   */
  getTrades: async (
    id: number,
    options?: { status?: 'open' | 'closed' | 'all'; limit?: number; offset?: number }
  ): Promise<AISystemTradesResponse> => {
    const params = {
      status: options?.status || 'all',
      limit: options?.limit || 100,
      offset: options?.offset || 0,
    };
    const response = await apiClient.get(
      `/ai/expert/api/systems/${id}/trades/${buildQueryString(params)}`
    );
    return response.data;
  },

  /**
   * Get comprehensive analytics for this AI system
   * @param id - System ID
   * @param days - Number of days for analytics period (default: 30)
   */
  getAnalytics: async (
    id: number,
    days?: number
  ): Promise<AISystemAnalyticsResponse> => {
    const params = days ? { days } : {};
    const response = await apiClient.get(
      `/ai/expert/api/systems/${id}/analytics/${buildQueryString(params)}`
    );
    return response.data;
  },
};

// =======================
// Expert API
// =======================

export const expertApi = {
  list: async (filters?: ExpertFilters): Promise<PaginatedResponse<ExpertListItem>> => {
    const response = await apiClient.get(`/ai/expert/api/experts/${buildQueryString(filters)}`);
    return response.data;
  },

  get: async (id: number): Promise<Expert> => {
    const response = await apiClient.get(`/ai/expert/api/experts/${id}/`);
    return response.data;
  },

  /**
   * Create an expert. Optional `params` can include query parameters
   * such as { ai_system: 123 } which will be appended to the URL.
   */
  create: async (data: ExpertCreate, params?: Record<string, any>): Promise<Expert> => {
    const query = buildQueryString(params);
    const response = await apiClient.post(`/ai/expert/api/experts/${query}`, data);
    return response.data;
  },

  update: async (id: number, data: Partial<ExpertCreate>): Promise<Expert> => {
    const response = await apiClient.patch(`/ai/expert/api/experts/${id}/`, data);
    return response.data;
  },

  /**
   * Soft delete an expert (sets is_deleted=True, can be restored).
   */
  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/ai/expert/api/experts/${id}/`);
  },

  /**
   * Restore a soft-deleted expert.
   */
  restore: async (id: number): Promise<{ success: boolean; message: string; expert: Expert }> => {
    const response = await apiClient.post(`/ai/expert/api/experts/${id}/restore/`);
    return response.data;
  },

  /**
   * Permanently delete an expert from database (irreversible).
   * Warning: This action cannot be undone.
   */
  hardDelete: async (id: number): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.delete(`/ai/expert/api/experts/${id}/hard_delete/`);
    return response.data;
  },

  testExecute: async (
    id: number, 
    inputData: Record<string, any>,
    expertOverrides?: Record<string, any>
  ): Promise<any> => {
    const response = await apiClient.post(
      `/ai/expert/api/experts/${id}/test_execute/`,
      { 
        input_data: inputData,
        expert_overrides: expertOverrides || undefined,
      },
      { timeout: EXECUTION_TIMEOUT } // Use longer timeout for execution
    );
    return response.data;
  },

  /**
   * Get the CODE expert executor configuration.
   * Used by the frontend documentation to stay in sync with backend.
   */
  getCodeExecutorConfig: async (): Promise<import('../types').CodeExecutorConfig> => {
    const response = await apiClient.get('/ai/expert/api/experts/code_executor_config/');
    return response.data;
  },

  /**
   * Get all available tools that CODE experts can call.
   * Returns tool names, descriptions, categories, and input schemas.
   */
  getAvailableTools: async (category?: string, search?: string): Promise<{
    tools: Array<{
      name: string;
      description: string;
      category: string;
      input_schema: Record<string, any>;
      requires_confirmation: boolean;
      danger_level: number;
    }>;
    categories: string[];
    total_count: number;
  }> => {
    const params = new URLSearchParams();
    if (category) params.append('category', category);
    if (search) params.append('search', search);
    const queryString = params.toString();
    const response = await apiClient.get(`/ai/expert/api/experts/available_tools/${queryString ? `?${queryString}` : ''}`);
    return response.data;
  },

  /**
   * Get action handlers the current user is allowed to configure.
   */
  getAvailableActions: async (): Promise<{
    actions: Array<{
      action_type: string;
      description: string;
      required_params: string[];
      optional_params: string[];
      feature_code: string;
      surface_key: string;
      param_descriptions: Record<string, string>;
      examples: any[];
    }>;
    total_count: number;
  }> => {
    const response = await apiClient.get('/ai/expert/api/experts/available_actions/');
    return response.data;
  },

  /**
   * Get expert input handlers the current user is allowed to configure.
   */
  getAvailableInputTypes: async (): Promise<{
    types: import('../types').SharedInputType[];
    total_count: number;
  }> => {
    const response = await apiClient.get('/ai/expert/api/experts/available_input_types/');
    return response.data;
  },

  /**
   * Get execution history for a specific expert.
   * Returns past execution results that can be used for override values.
   */
  getExecutionHistory: async (expertId: number, limit?: number): Promise<import('../types').ExpertExecutionResult[]> => {
    const params = new URLSearchParams();
    params.append('expert', String(expertId));
    params.append('ordering', '-created_at');
    if (limit) {
      params.append('page_size', String(limit));
    }
    const response = await apiClient.get(`/ai/expert/api/expert-results/?${params.toString()}`);
    return response.data.results || response.data;
  },

  /**
   * Get output keys for an expert's output schema.
   * Used to build {{ expert_name.key }} references in input configs.
   */
  getOutputKeys: async (expertId: number): Promise<{
    expert_id: number;
    expert_name: string;
    output_keys: Array<{ key: string; type: string; required: boolean; description: string }>;
  }> => {
    const response = await apiClient.get(`/ai/expert/api/experts/${expertId}/output_keys/`);
    return response.data;
  },
};

// =======================
// AI Schedule API
// =======================

export const aiScheduleApi = {
  list: async (filters?: AIScheduleFilters): Promise<PaginatedResponse<AISchedule>> => {
    const response = await apiClient.get(`/ai/expert/api/schedules/${buildQueryString(filters)}`);
    return response.data;
  },

  get: async (id: number): Promise<AISchedule> => {
    const response = await apiClient.get(`/ai/expert/api/schedules/${id}/`);
    return response.data;
  },

  create: async (data: AIScheduleCreate): Promise<AISchedule> => {
    const response = await apiClient.post('/ai/expert/api/schedules/', data);
    return response.data;
  },

  update: async (id: number, data: Partial<AIScheduleCreate>): Promise<AISchedule> => {
    const response = await apiClient.patch(`/ai/expert/api/schedules/${id}/`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/ai/expert/api/schedules/${id}/`);
  },

  activate: async (id: number): Promise<AISchedule> => {
    const response = await apiClient.post(`/ai/expert/api/schedules/${id}/activate/`);
    return response.data;
  },

  deactivate: async (id: number): Promise<AISchedule> => {
    const response = await apiClient.post(`/ai/expert/api/schedules/${id}/deactivate/`);
    return response.data;
  },
};

// =======================
// Model Lab API
// =======================

export const modelLabDatasetApi = {
  list: async (
    filters?: ModelLabDatasetFilters
  ): Promise<PaginatedResponse<ModelLabDataset>> => {
    const response = await apiClient.get(
      `/ai/model-lab/api/datasets/${buildQueryString(filters)}`
    );
    return response.data;
  },

  get: async (id: number): Promise<ModelLabDataset> => {
    const response = await apiClient.get(`/ai/model-lab/api/datasets/${id}/`);
    return response.data;
  },

  create: async (data: ModelLabDatasetCreate): Promise<ModelLabDataset> => {
    const response = await apiClient.post('/ai/model-lab/api/datasets/', data);
    return response.data;
  },

  update: async (
    id: number,
    data: Partial<ModelLabDatasetCreate>
  ): Promise<ModelLabDataset> => {
    const response = await apiClient.patch(
      `/ai/model-lab/api/datasets/${id}/`,
      data
    );
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/ai/model-lab/api/datasets/${id}/`);
  },

  getVersions: async (id: number): Promise<ModelLabDatasetVersion[]> => {
    const response = await apiClient.get(`/ai/model-lab/api/datasets/${id}/versions/`);
    return response.data;
  },

  createVersion: async (
    id: number,
    data: ModelLabDatasetVersionCreate
  ): Promise<ModelLabDatasetVersion> => {
    const response = await apiClient.post(
      `/ai/model-lab/api/datasets/${id}/versions/`,
      data
    );
    return response.data;
  },

  uploadCsv: async (id: number, file: File, name?: string, description?: string): Promise<ModelLabDatasetVersion> => {
    const formData = new FormData();
    formData.append('file', file);
    if (name) formData.append('name', name);
    if (description) formData.append('description', description);
    const response = await apiClient.post(
      `/ai/model-lab/api/datasets/${id}/upload-csv/`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  },

  importMarketData: async (
    id: number,
    params: { symbol: string; timeframe?: string; start?: string; end?: string; include_indicators?: boolean; name?: string }
  ): Promise<ModelLabDatasetVersion> => {
    const response = await apiClient.post(
      `/ai/model-lab/api/datasets/${id}/import-market-data/`,
      params
    );
    return response.data;
  },

  mergeVersions: async (data: {
    version_ids: number[];
    strategy: string;
    join_column?: string;
    target_dataset_id: number;
    target_version_name: string;
  }): Promise<ModelLabDatasetVersion> => {
    const response = await apiClient.post(
      '/ai/model-lab/api/datasets/merge/',
      data
    );
    return response.data;
  },
};

export const modelLabDatasetVersionApi = {
  list: async (
    filters?: ModelLabDatasetVersionFilters
  ): Promise<PaginatedResponse<ModelLabDatasetVersion>> => {
    const response = await apiClient.get(
      `/ai/model-lab/api/dataset-versions/${buildQueryString(filters)}`
    );
    return response.data;
  },

  get: async (id: number): Promise<ModelLabDatasetVersion> => {
    const response = await apiClient.get(`/ai/model-lab/api/dataset-versions/${id}/`);
    return response.data;
  },

  profile: async (id: number): Promise<DataProfile> => {
    const response = await apiClient.get(`/ai/model-lab/api/dataset-versions/${id}/profile/`);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/ai/model-lab/api/dataset-versions/${id}/`);
  },
};

export const modelLabFeatureSetApi = {
  list: async (
    filters?: ModelLabFeatureSetFilters
  ): Promise<PaginatedResponse<ModelLabFeatureSet>> => {
    const response = await apiClient.get(
      `/ai/model-lab/api/feature-sets/${buildQueryString(filters)}`
    );
    return response.data;
  },

  get: async (id: number): Promise<ModelLabFeatureSet> => {
    const response = await apiClient.get(`/ai/model-lab/api/feature-sets/${id}/`);
    return response.data;
  },

  create: async (data: ModelLabFeatureSetCreate): Promise<ModelLabFeatureSet> => {
    const response = await apiClient.post('/ai/model-lab/api/feature-sets/', data);
    return response.data;
  },

  update: async (
    id: number,
    data: Partial<ModelLabFeatureSetCreate>
  ): Promise<ModelLabFeatureSet> => {
    const response = await apiClient.patch(`/ai/model-lab/api/feature-sets/${id}/`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/ai/model-lab/api/feature-sets/${id}/`);
  },

  preview: async (id: number): Promise<FeaturePreview> => {
    const response = await apiClient.get(`/ai/model-lab/api/feature-sets/${id}/preview/`);
    return response.data;
  },
};

export const modelLabAssetApi = {
  list: async (
    filters?: ModelLabAssetFilters
  ): Promise<PaginatedResponse<ModelLabAsset>> => {
    const response = await apiClient.get(
      `/ai/model-lab/api/model-assets/${buildQueryString(filters)}`
    );
    return response.data;
  },

  get: async (id: number): Promise<ModelLabAsset> => {
    const response = await apiClient.get(`/ai/model-lab/api/model-assets/${id}/`);
    return response.data;
  },

  create: async (data: ModelLabAssetCreate): Promise<ModelLabAsset> => {
    const response = await apiClient.post('/ai/model-lab/api/model-assets/', data);
    return response.data;
  },

  update: async (
    id: number,
    data: Partial<ModelLabAssetCreate>
  ): Promise<ModelLabAsset> => {
    const response = await apiClient.patch(
      `/ai/model-lab/api/model-assets/${id}/`,
      data
    );
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/ai/model-lab/api/model-assets/${id}/`);
  },

  getVersions: async (id: number): Promise<ModelLabVersion[]> => {
    const response = await apiClient.get(`/ai/model-lab/api/model-assets/${id}/versions/`);
    return response.data;
  },
};

export const modelLabVersionApi = {
  list: async (
    filters?: ModelLabVersionFilters
  ): Promise<PaginatedResponse<ModelLabVersion>> => {
    const response = await apiClient.get(
      `/ai/model-lab/api/model-versions/${buildQueryString(filters)}`
    );
    return response.data;
  },

  get: async (id: number): Promise<ModelLabVersion> => {
    const response = await apiClient.get(`/ai/model-lab/api/model-versions/${id}/`);
    return response.data;
  },

  promote: async (id: number): Promise<ModelLabVersion> => {
    const response = await apiClient.post(`/ai/model-lab/api/model-versions/${id}/promote/`);
    return response.data;
  },

  demote: async (id: number): Promise<ModelLabVersion> => {
    const response = await apiClient.post(`/ai/model-lab/api/model-versions/${id}/demote/`);
    return response.data;
  },

  downloadArtifact: async (id: number): Promise<Blob> => {
    const response = await apiClient.get(
      `/ai/model-lab/api/model-versions/${id}/download-artifact/`,
      { responseType: 'blob' }
    );
    return response.data;
  },

  lineage: async (id: number): Promise<ModelLineage> => {
    const response = await apiClient.get(`/ai/model-lab/api/model-versions/${id}/lineage/`);
    return response.data;
  },

  internals: async (
    id: number,
    params?: { max_trees?: number; max_nodes?: number }
  ): Promise<ModelInternals> => {
    const response = await apiClient.get(
      `/ai/model-lab/api/model-versions/${id}/internals/${buildQueryString(params)}`
    );
    return response.data;
  },
};

export const modelLabTrainingJobApi = {
  list: async (
    filters?: ModelLabTrainingJobFilters
  ): Promise<PaginatedResponse<ModelLabTrainingJob>> => {
    const response = await apiClient.get(
      `/ai/model-lab/api/training-jobs/${buildQueryString(filters)}`
    );
    return response.data;
  },

  get: async (id: number): Promise<ModelLabTrainingJob> => {
    const response = await apiClient.get(`/ai/model-lab/api/training-jobs/${id}/`);
    return response.data;
  },

  create: async (
    data: ModelLabTrainingJobCreate
  ): Promise<ModelLabTrainingJob> => {
    const response = await apiClient.post('/ai/model-lab/api/training-jobs/', data);
    return response.data;
  },

  update: async (
    id: number,
    data: Partial<ModelLabTrainingJobCreate>
  ): Promise<ModelLabTrainingJob> => {
    const response = await apiClient.patch(
      `/ai/model-lab/api/training-jobs/${id}/`,
      data
    );
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/ai/model-lab/api/training-jobs/${id}/`);
  },

  launch: async (id: number, computeSpecId?: number | null): Promise<ModelLabTrainingJob> => {
    const response = await apiClient.post(
      `/ai/model-lab/api/training-jobs/${id}/launch/`,
      computeSpecId ? { compute_spec_id: computeSpecId } : undefined,
    );
    return response.data;
  },

  cancel: async (id: number): Promise<ModelLabTrainingJob> => {
    const response = await apiClient.post(`/ai/model-lab/api/training-jobs/${id}/cancel/`);
    return response.data;
  },

  compare: async (params: { job_ids?: number[]; version_ids?: number[] }): Promise<{ comparisons: ExperimentComparison[] }> => {
    const response = await apiClient.post('/ai/model-lab/api/training-jobs/compare/', params);
    return response.data;
  },
};

export const modelLabDeploymentApi = {
  list: async (
    filters?: ModelLabDeploymentFilters
  ): Promise<PaginatedResponse<ModelLabDeployment>> => {
    const response = await apiClient.get(
      `/ai/model-lab/api/deployments/${buildQueryString(filters)}`
    );
    return response.data;
  },

  get: async (id: number): Promise<ModelLabDeployment> => {
    const response = await apiClient.get(`/ai/model-lab/api/deployments/${id}/`);
    return response.data;
  },

  create: async (
    data: ModelLabDeploymentCreate
  ): Promise<ModelLabDeployment> => {
    const response = await apiClient.post('/ai/model-lab/api/deployments/', data);
    return response.data;
  },

  update: async (
    id: number,
    data: Partial<ModelLabDeploymentCreate>
  ): Promise<ModelLabDeployment> => {
    const response = await apiClient.patch(
      `/ai/model-lab/api/deployments/${id}/`,
      data
    );
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/ai/model-lab/api/deployments/${id}/`);
  },

  activate: async (id: number): Promise<ModelLabDeployment> => {
    const response = await apiClient.post(`/ai/model-lab/api/deployments/${id}/activate/`);
    return response.data;
  },

  pause: async (id: number): Promise<ModelLabDeployment> => {
    const response = await apiClient.post(`/ai/model-lab/api/deployments/${id}/pause/`);
    return response.data;
  },

  predict: async (id: number, input: Record<string, any> | Record<string, any>[]): Promise<PredictionResult> => {
    const response = await apiClient.post(`/ai/model-lab/api/deployments/${id}/predict/`, { input });
    return response.data;
  },

  predictLive: async (
    id: number,
    options?: { rows?: number; symbol?: string; timeframe?: string; lookback_bars?: number }
  ): Promise<any> => {
    const response = await apiClient.post(
      `/ai/model-lab/api/deployments/${id}/predict-live/`,
      options || {}
    );
    return response.data;
  },

  pipelines: async (): Promise<Array<{ name: string; description: string; default_features: string[] }>> => {
    const response = await apiClient.get(`/ai/model-lab/api/deployments/pipelines/`);
    return response.data?.pipelines || response.data;
  },

  rollback: async (id: number, versionId: number): Promise<ModelLabDeployment> => {
    const response = await apiClient.post(`/ai/model-lab/api/deployments/${id}/rollback/`, { version_id: versionId });
    return response.data;
  },

  healthCheck: async (id: number): Promise<DeploymentHealth> => {
    const response = await apiClient.get(`/ai/model-lab/api/deployments/${id}/health-check/`);
    return response.data;
  },
};

// =======================
// HP Tuning API
// =======================
export const modelLabHPTuningApi = {
  list: async (filters?: ModelLabHPTuningJobFilters): Promise<PaginatedResponse<ModelLabHPTuningJob>> => {
    const response = await apiClient.get(`/ai/model-lab/api/hp-tuning-jobs/${buildQueryString(filters)}`);
    return response.data;
  },
  get: async (id: number): Promise<ModelLabHPTuningJob> => {
    const response = await apiClient.get(`/ai/model-lab/api/hp-tuning-jobs/${id}/`);
    return response.data;
  },
  create: async (data: ModelLabHPTuningJobCreate): Promise<ModelLabHPTuningJob> => {
    const response = await apiClient.post('/ai/model-lab/api/hp-tuning-jobs/', data);
    return response.data;
  },
  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/ai/model-lab/api/hp-tuning-jobs/${id}/`);
  },
  launch: async (id: number): Promise<ModelLabHPTuningJob> => {
    const response = await apiClient.post(`/ai/model-lab/api/hp-tuning-jobs/${id}/launch/`);
    return response.data;
  },
  cancel: async (id: number): Promise<ModelLabHPTuningJob> => {
    const response = await apiClient.post(`/ai/model-lab/api/hp-tuning-jobs/${id}/cancel/`);
    return response.data;
  },
};

// =======================
// AutoML API
// =======================
export const modelLabAutoMLApi = {
  list: async (filters?: ModelLabAutoMLRunFilters): Promise<PaginatedResponse<ModelLabAutoMLRun>> => {
    const response = await apiClient.get(`/ai/model-lab/api/automl-runs/${buildQueryString(filters)}`);
    return response.data;
  },
  get: async (id: number): Promise<ModelLabAutoMLRun> => {
    const response = await apiClient.get(`/ai/model-lab/api/automl-runs/${id}/`);
    return response.data;
  },
  create: async (data: ModelLabAutoMLRunCreate): Promise<ModelLabAutoMLRun> => {
    const response = await apiClient.post('/ai/model-lab/api/automl-runs/', data);
    return response.data;
  },
  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/ai/model-lab/api/automl-runs/${id}/`);
  },
  launch: async (id: number): Promise<ModelLabAutoMLRun> => {
    const response = await apiClient.post(`/ai/model-lab/api/automl-runs/${id}/launch/`);
    return response.data;
  },
};

// =======================
// Explainability API
// =======================
export const modelLabExplainabilityApi = {
  list: async (filters?: { status?: string; method?: string }): Promise<PaginatedResponse<ModelLabExplainabilityReport>> => {
    const response = await apiClient.get(`/ai/model-lab/api/explainability-reports/${buildQueryString(filters)}`);
    return response.data;
  },
  get: async (id: number): Promise<ModelLabExplainabilityReport> => {
    const response = await apiClient.get(`/ai/model-lab/api/explainability-reports/${id}/`);
    return response.data;
  },
  create: async (data: ModelLabExplainabilityReportCreate): Promise<ModelLabExplainabilityReport> => {
    const response = await apiClient.post('/ai/model-lab/api/explainability-reports/', data);
    return response.data;
  },
  compute: async (id: number): Promise<ModelLabExplainabilityReport> => {
    const response = await apiClient.post(`/ai/model-lab/api/explainability-reports/${id}/compute/`);
    return response.data;
  },
};

// =======================
// Model Monitor API
// =======================
export const modelLabMonitorApi = {
  list: async (filters?: { is_active?: boolean; deployment?: number }): Promise<PaginatedResponse<ModelLabModelMonitor>> => {
    const response = await apiClient.get(`/ai/model-lab/api/model-monitors/${buildQueryString(filters)}`);
    return response.data;
  },
  get: async (id: number): Promise<ModelLabModelMonitor> => {
    const response = await apiClient.get(`/ai/model-lab/api/model-monitors/${id}/`);
    return response.data;
  },
  create: async (data: ModelLabModelMonitorCreate): Promise<ModelLabModelMonitor> => {
    const response = await apiClient.post('/ai/model-lab/api/model-monitors/', data);
    return response.data;
  },
  update: async (id: number, data: Partial<ModelLabModelMonitorCreate>): Promise<ModelLabModelMonitor> => {
    const response = await apiClient.patch(`/ai/model-lab/api/model-monitors/${id}/`, data);
    return response.data;
  },
  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/ai/model-lab/api/model-monitors/${id}/`);
  },
  runCheck: async (id: number): Promise<ModelLabModelMonitor> => {
    const response = await apiClient.post(`/ai/model-lab/api/model-monitors/${id}/run-check/`);
    return response.data;
  },
  reports: async (id: number): Promise<ModelLabDriftReport[]> => {
    const response = await apiClient.get(`/ai/model-lab/api/model-monitors/${id}/reports/`);
    return response.data;
  },
};

// =======================
// Drift Report API (read-only)
// =======================
export const modelLabDriftReportApi = {
  list: async (filters?: { monitor?: number; status?: string }): Promise<PaginatedResponse<ModelLabDriftReport>> => {
    const response = await apiClient.get(`/ai/model-lab/api/drift-reports/${buildQueryString(filters)}`);
    return response.data;
  },
  get: async (id: number): Promise<ModelLabDriftReport> => {
    const response = await apiClient.get(`/ai/model-lab/api/drift-reports/${id}/`);
    return response.data;
  },
};

// =======================
// Prediction Log API (read-only)
// =======================
export const modelLabPredictionLogApi = {
  list: async (filters?: { deployment?: number }): Promise<PaginatedResponse<ModelLabPredictionLog>> => {
    const response = await apiClient.get(`/ai/model-lab/api/prediction-logs/${buildQueryString(filters)}`);
    return response.data;
  },
};

// =======================
// RL Training API
// =======================
export const modelLabRLTrainingApi = {
  list: async (filters?: { status?: string; algorithm?: string }): Promise<PaginatedResponse<ModelLabRLTrainingJob>> => {
    const response = await apiClient.get(`/ai/model-lab/api/rl-training-jobs/${buildQueryString(filters)}`);
    return response.data;
  },
  get: async (id: number): Promise<ModelLabRLTrainingJob> => {
    const response = await apiClient.get(`/ai/model-lab/api/rl-training-jobs/${id}/`);
    return response.data;
  },
  create: async (data: ModelLabRLTrainingJobCreate): Promise<ModelLabRLTrainingJob> => {
    const response = await apiClient.post('/ai/model-lab/api/rl-training-jobs/', data);
    return response.data;
  },
  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/ai/model-lab/api/rl-training-jobs/${id}/`);
  },
  launch: async (id: number): Promise<ModelLabRLTrainingJob> => {
    const response = await apiClient.post(`/ai/model-lab/api/rl-training-jobs/${id}/launch/`);
    return response.data;
  },
  cancel: async (id: number): Promise<ModelLabRLTrainingJob> => {
    const response = await apiClient.post(`/ai/model-lab/api/rl-training-jobs/${id}/cancel/`);
    return response.data;
  },
};

// =======================
// Execution API
// =======================

export interface RerunExpertResponse {
  success: boolean;
  result?: ExpertExecutionResult;
  message?: string;
  error?: string;
  error_type?: string;
}

export interface ExecutionOperatorResponse {
  success: boolean;
  mode?: 'durable_graph_retry' | 'durable_graph_rerun';
  execution?: AISystemExecution;
  message?: string;
  error?: string;
  error_code?: string;
  queued?: boolean;
  created?: boolean;
  replayed?: boolean;
  redispatched?: boolean;
  workflow_run_id?: string;
  workflow_status?: string;
  source_execution_id?: string;
  source_workflow_run_id?: string;
  task_id?: string;
  dispatch_status?: string;
  retried_experts?: number;
  logs?: string[];
}

export const createExecutionOperatorIdempotencyKey = (
  operation: 'retry' | 'rerun',
  executionId: string | number,
): string => {
  const randomPart = globalThis.crypto?.randomUUID?.()
    || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `execution-${operation}-${executionId}-${randomPart}`;
};

export const executionApi = {
  list: async (filters?: ExecutionFilters): Promise<PaginatedResponse<AISystemExecutionListItem>> => {
    const response = await apiClient.get(`/ai/expert/api/executions/${buildQueryString(filters)}`);
    return response.data;
  },

  get: async (id: string | number): Promise<AISystemExecution> => {
    const response = await apiClient.get(`/ai/expert/api/executions/${id}/`);
    return response.data;
  },

  getExpertResults: async (id: string | number): Promise<ExpertExecutionResult[]> => {
    const response = await apiClient.get(`/ai/expert/api/executions/${id}/expert_results/`);
    return response.data;
  },

  /**
   * Rerun a single expert within an execution context
   * @param executionId - The execution ID
   * @param expertId - The expert ID to rerun
   * @param options - Options for rerun (skip_cache, use_original_context)
   */
  rerunExpert: async (
    executionId: string | number,
    expertId: number,
    options?: { skip_cache?: boolean; use_original_context?: boolean }
  ): Promise<RerunExpertResponse> => {
    const response = await apiClient.post(
      `/ai/expert/api/executions/${executionId}/rerun_expert/${expertId}/`,
      options || {},
      { timeout: EXECUTION_TIMEOUT }
    );
    return response.data;
  },

  /**
   * Retry a failed execution from the point of failure
   * @param executionId - The execution ID to retry
   * @param options - Options for retry (skip_cache, from_expert_id)
   */
  retryExecution: async (
    executionId: string | number,
    options?: {
      skip_cache?: boolean;
      from_expert_id?: string;
      idempotency_key?: string;
    }
  ): Promise<ExecutionOperatorResponse> => {
    const headers = options?.idempotency_key
      ? { 'Idempotency-Key': options.idempotency_key }
      : undefined;
    const response = await apiClient.post(
      `/ai/expert/api/executions/${executionId}/retry/`,
      options || {},
      { timeout: EXECUTION_TIMEOUT, headers }
    );
    return response.data;
  },

  /** Create a fresh execution from a terminal graph-owned run. */
  rerunExecution: async (
    executionId: string | number,
    options: { idempotency_key: string }
  ): Promise<ExecutionOperatorResponse> => {
    const response = await apiClient.post(
      `/ai/expert/api/executions/${executionId}/rerun/`,
      {},
      {
        timeout: EXECUTION_TIMEOUT,
        headers: { 'Idempotency-Key': options.idempotency_key },
      }
    );
    return response.data;
  },

  /**
   * Stop a running execution
   * @param executionId - The execution ID to stop
   */
  stopExecution: async (
    executionId: string | number
  ): Promise<{
    success: boolean;
    execution?: AISystemExecution;
    message?: string;
    error?: string;
  }> => {
    const response = await apiClient.post(
      `/ai/expert/api/executions/${executionId}/stop/`
    );
    return response.data;
  },

  stats: async (): Promise<{
    total_executions: number;
    by_status: Record<string, number>;
    by_type: Record<string, number>;
    recent: {
      last_24h: number;
      last_7d: number;
      last_30d: number;
    };
    success_rate: number;
  }> => {
    const response = await apiClient.get('/ai/expert/api/executions/stats/');
    return response.data;
  },

  /**
   * Get available action types and tool names for filtering
   */
  actionTypes: async (): Promise<{
    action_types: string[];
    tool_names: string[];
    all_types: string[];
  }> => {
    const response = await apiClient.get('/ai/expert/api/executions/action_types/');
    return response.data;
  },
};

// =======================
// Expert Result API
// =======================

export const expertResultApi = {
  list: async (filters?: Record<string, any>): Promise<PaginatedResponse<ExpertExecutionResult>> => {
    const response = await apiClient.get(`/ai/expert/api/expert-results/${buildQueryString(filters)}`);
    return response.data;
  },

  get: async (id: number): Promise<ExpertExecutionResult> => {
    const response = await apiClient.get(`/ai/expert/api/expert-results/${id}/`);
    return response.data;
  },
};

// =======================
// Account API
// =======================

export interface Account {
  id: string;  // UUID
  simple_id: number;  // 6-digit code
  name: string;
  platform: 'MT5' | 'cTrader';
  balance: number;
  equity: number;
  created_at: string;
  active: boolean;
}

export const accountApi = {
  list: async (): Promise<Account[]> => {
    const response = await apiClient.get('/accounts/');
    return response.data;
  },
};

// =======================
// Indicator API
// =======================

export interface IndicatorInfo {
  params_schema: Record<string, { type: string; default: any; description?: string }>;
  outputs: string[];
  visual_schema?: Record<string, any>;
  category?: string;
}

export const indicatorApi = {
  getAvailable: async (): Promise<Record<string, IndicatorInfo>> => {
    const response = await apiClient.get('/ai/indicators/available/');
    return response.data.indicators;
  },
};

// =======================
// Snippet API
// =======================

import type {
  Snippet,
  SnippetCreate,
  SnippetListItem,
  SnippetDetail,
  SnippetFilters,
  SnippetValidationResult,
  SnippetProcessResult,
} from '../types';

export const snippetApi = {
  // List all snippets with optional filtering
  list: async (filters?: SnippetFilters): Promise<PaginatedResponse<SnippetListItem>> => {
    const params = new URLSearchParams();
    if (filters?.category) params.append('category', filters.category);
    if (filters?.is_active !== undefined) params.append('is_active', filters.is_active.toString());
    if (filters?.search) params.append('search', filters.search);
    
    const response = await apiClient.get(`/ai/expert/api/snippets/?${params.toString()}`);
    return response.data;
  },

  // Get single snippet details
  get: async (id: number): Promise<SnippetDetail> => {
    const response = await apiClient.get(`/ai/expert/api/snippets/${id}/`);
    return response.data;
  },

  // Create new snippet
  create: async (data: SnippetCreate): Promise<Snippet> => {
    const response = await apiClient.post('/ai/expert/api/snippets/', data);
    return response.data;
  },

  // Update existing snippet
  update: async (id: number, data: Partial<SnippetCreate>): Promise<Snippet> => {
    const response = await apiClient.patch(`/ai/expert/api/snippets/${id}/`, data);
    return response.data;
  },

  // Delete snippet
  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/ai/expert/api/snippets/${id}/`);
  },

  // Duplicate snippet
  duplicate: async (id: number, newName: string): Promise<Snippet> => {
    const response = await apiClient.post(`/ai/expert/api/snippets/${id}/duplicate/`, { name: newName });
    return response.data;
  },

  // Validate snippet syntax in text
  validateSyntax: async (text: string): Promise<SnippetValidationResult> => {
    const response = await apiClient.post('/ai/expert/api/snippets/validate_syntax/', { text });
    return response.data;
  },

  // Process snippet references in text
  process: async (text: string, failOnMissing: boolean = false): Promise<SnippetProcessResult> => {
    const response = await apiClient.post('/ai/expert/api/snippets/process/', {
      text,
      fail_on_missing: failOnMissing,
    });
    return response.data;
  },

  // Get all categories
  getCategories: async (): Promise<string[]> => {
    const response = await apiClient.get('/ai/expert/api/snippets/categories/');
    return response.data.categories;
  },

  // Get all tags
  getTags: async (): Promise<string[]> => {
    const response = await apiClient.get('/ai/expert/api/snippets/tags/');
    return response.data.tags;
  },
};

// Indicator Set API
// =======================

import type {
  IndicatorSet,
  IndicatorSetCreate,
  IndicatorSetListItem,
  IndicatorSetDetail,
  IndicatorSetFilters,
} from '../types';

export const indicatorSetApi = {
  // List all indicator sets with optional filtering
  list: async (filters?: IndicatorSetFilters): Promise<PaginatedResponse<IndicatorSetListItem>> => {
    const params = new URLSearchParams();
    if (filters?.category) params.append('category', filters.category);
    if (filters?.is_active !== undefined) params.append('is_active', filters.is_active.toString());
    if (filters?.search) params.append('search', filters.search);
    
    const response = await apiClient.get(`/ai/expert/api/indicator-sets/?${params.toString()}`);
    return response.data;
  },

  // Get single indicator set details
  get: async (id: number): Promise<IndicatorSetDetail> => {
    const response = await apiClient.get(`/ai/expert/api/indicator-sets/${id}/`);
    return response.data;
  },

  // Create new indicator set
  create: async (data: IndicatorSetCreate): Promise<IndicatorSet> => {
    const response = await apiClient.post('/ai/expert/api/indicator-sets/', data);
    return response.data;
  },

  // Update existing indicator set
  update: async (id: number, data: Partial<IndicatorSetCreate>): Promise<IndicatorSet> => {
    const response = await apiClient.patch(`/ai/expert/api/indicator-sets/${id}/`, data);
    return response.data;
  },

  // Delete indicator set
  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/ai/expert/api/indicator-sets/${id}/`);
  },

  // Duplicate indicator set (name is optional, auto-generated if not provided)
  duplicate: async (id: number, newName?: string): Promise<IndicatorSet> => {
    const response = await apiClient.post(`/ai/expert/api/indicator-sets/${id}/duplicate/`, 
      newName ? { name: newName } : {}
    );
    return response.data;
  },

  // Get all categories
  getCategories: async (): Promise<string[]> => {
    const response = await apiClient.get('/ai/expert/api/indicator-sets/categories/');
    return response.data.categories;
  },

  // Get all tags
  getTags: async (): Promise<string[]> => {
    const response = await apiClient.get('/ai/expert/api/indicator-sets/tags/');
    return response.data.tags;
  },
};

// Agent System API
// =======================

import type {
  Agent,
  AgentCreate,
  AgentUpdate,
  AgentDetail,
  AgentListItem,
  PaginatedAgentResponse,
  Tool,
  ToolInfo,
  AvailableTools,
  ToolAssignmentRequest,
  SubAgentAssignment,
  SubAgentAssignmentRequest,
  SubAgentAssignmentUpdate,
  AvailableSubAgents,
  AgentExecution,
  AgentExecutionSummary,
  AgentExecutionListItem,
  PaginatedExecutionResponse,
  AgentExecuteRequest,
  ConversationMessage,
  AgentMemory,
  AgentMemoryCreate,
  AgentMemoryUpdate,
  PaginatedMemoryResponse,
  AgentMemoryStats,
  MemorySearchFilters,
  ConversationSession,
  ConversationSessionListItem,
  ConversationSessionCreate,
  ConversationSessionUpdate,
  PaginatedSessionResponse,
  SyncAgentRequest,
  SyncAgentResponse,
  BulkSyncRequest,
  BulkSyncResponse,
  MessageAttachment,
  SessionSnapshotsResponse,
  MessageSnapshotResponse,
  RestoreSnapshotOptions,
  RestoreSnapshotResponse,
  BuilderIntentLedgerResponse,
  BuilderIntentItemPatchRequest,
  BuilderIntentLedgerEventRequest,
  BuilderArtifactWorkflowResponse,
  BuilderArtifactWorkflowActionResponse,
} from '../types/agent';

export const agentApi = {
  // Agent CRUD
  // =======================
  
  // List all agents
  list: async (page: number = 1, pageSize: number = 20, filters?: { is_active?: boolean; is_template?: boolean }): Promise<PaginatedAgentResponse> => {
    const response = await apiClient.get('/ai/expert/api/agent-system/agents/', {
      params: { 
        page, 
        page_size: pageSize,
        ...filters,
      },
    });
    return response.data;
  },

  // List template agents only (for provisioning)
  listTemplates: async (): Promise<AgentListItem[]> => {
    const response = await apiClient.get('/ai/expert/api/agent-system/agents/', {
      params: { is_template: true, page_size: 100 },
    });
    return response.data.results || [];
  },

  // Get single agent details
  get: async (id: number): Promise<AgentDetail> => {
    const response = await apiClient.get(`/ai/expert/api/agent-system/agents/${id}/`);
    return response.data;
  },

  // Create new agent
  create: async (data: AgentCreate): Promise<AgentDetail> => {
    const response = await apiClient.post('/ai/expert/api/agent-system/agents/', data);
    return response.data;
  },

  // Update agent
  update: async (id: number, data: AgentUpdate): Promise<AgentDetail> => {
    const response = await apiClient.patch(`/ai/expert/api/agent-system/agents/${id}/`, data);
    return response.data;
  },

  // Delete agent
  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/ai/expert/api/agent-system/agents/${id}/`);
  },

  // Get system builder agent
  getSystemBuilder: async (): Promise<AgentDetail> => {
    const response = await apiClient.get('/ai/expert/api/agent-system/agents/system-builder/');
    return response.data;
  },

  // Get system architect runtime agent
  getSystemArchitect: async (): Promise<AgentDetail> => {
    const response = await apiClient.get('/ai/expert/api/agent-system/agents/system-architect/');
    return response.data;
  },

  getBuilderIntentLedger: async (params: { sessionId?: string | null; aiSystemId?: number | string | null }): Promise<BuilderIntentLedgerResponse> => {
    const response = await apiClient.get('/ai/expert/api/agent-system/builder/ledgers/active/', {
      params: {
        session_id: params.sessionId || undefined,
        ai_system_id: params.aiSystemId || undefined,
      },
    });
    return response.data;
  },

  updateBuilderIntentItem: async (params: { ledgerId: string; itemId: string; patch: BuilderIntentItemPatchRequest }): Promise<BuilderIntentLedgerResponse> => {
    const response = await apiClient.patch(
      `/ai/expert/api/agent-system/builder/ledgers/${params.ledgerId}/items/${params.itemId}/`,
      params.patch,
    );
    return response.data;
  },

  appendBuilderIntentLedgerEvent: async (params: { ledgerId: string; event: BuilderIntentLedgerEventRequest }): Promise<BuilderIntentLedgerResponse> => {
    const response = await apiClient.post(
      `/ai/expert/api/agent-system/builder/ledgers/${params.ledgerId}/events/`,
      params.event,
    );
    return response.data;
  },

  getActiveBuilderArtifact: async (params: { sessionId?: string | null; aiSystemId?: number | string | null }): Promise<BuilderArtifactWorkflowResponse> => {
    const response = await apiClient.get('/ai/expert/api/agent-system/builder/artifacts/active/', {
      params: {
        session_id: params.sessionId || undefined,
        ai_system_id: params.aiSystemId || undefined,
      },
    });
    return response.data;
  },

  approveBuilderArtifact: async (params: { artifactId: string; approvalReference: Record<string, any> }): Promise<BuilderArtifactWorkflowActionResponse> => {
    const response = await apiClient.post(
      `/ai/expert/api/agent-system/builder/artifacts/${params.artifactId}/approve/`,
      { approval_reference: params.approvalReference }
    );
    return response.data;
  },

  applyBuilderArtifact: async (params: { artifactId: string; approvalReference: Record<string, any> }): Promise<BuilderArtifactWorkflowActionResponse> => {
    const response = await apiClient.post(
      `/ai/expert/api/agent-system/builder/artifacts/${params.artifactId}/apply/`,
      { approval_reference: params.approvalReference }
    );
    return response.data;
  },

  // Get bot builder agent
  getBotBuilder: async (): Promise<AgentDetail> => {
    const response = await apiClient.get('/ai/expert/api/agent-system/agents/bot-builder/');
    return response.data;
  },

  // Get model lab builder agent (admin only)
  getModelLabBuilder: async (): Promise<AgentDetail> => {
    const response = await apiClient.get('/ai/expert/api/agent-system/agents/model-lab-builder/');
    return response.data;
  },

  // Template Sync Management
  // =======================

  // Sync agent from template
  syncFromTemplate: async (agentId: number, request: SyncAgentRequest = {}): Promise<SyncAgentResponse> => {
    const response = await apiClient.post(
      `/ai/expert/api/agent-system/agents/${agentId}/sync_from_template/`,
      request
    );
    return response.data;
  },

  // Reset agent to template defaults (admin only)
  resetToTemplate: async (agentId: number): Promise<SyncAgentResponse> => {
    const response = await apiClient.post(
      `/ai/expert/api/agent-system/agents/${agentId}/reset_to_template/`
    );
    return response.data;
  },

  // Update all agents provisioned from template (admin only)
  updateProvisionedAgents: async (agentId: number, request: { force?: boolean; async?: boolean } = {}): Promise<SyncAgentResponse> => {
    const response = await apiClient.post(
      `/ai/expert/api/agent-system/agents/${agentId}/update-provisioned-agents/`,
      request
    );
    return response.data;
  },

  // Bulk sync all template instances (admin only)
  syncAllTemplates: async (request: BulkSyncRequest): Promise<BulkSyncResponse> => {
    const response = await apiClient.post(
      '/ai/expert/api/agent-system/agents/sync-all-templates/',
      request
    );
    return response.data;
  },

  // Tool Management
  // =======================

  // Get available and assigned tools
  getTools: async (agentId: number): Promise<AvailableTools> => {
    const response = await apiClient.get(`/ai/expert/api/agent-system/agents/${agentId}/tools/`);
    return response.data;
  },

  // Assign tool to agent
  assignTool: async (agentId: number, data: ToolAssignmentRequest): Promise<{ message: string }> => {
    const response = await apiClient.post(
      `/ai/expert/api/agent-system/agents/${agentId}/assign_tool/`,
      data
    );
    return response.data;
  },

  // Unassign tool from agent
  unassignTool: async (agentId: number, toolName: string): Promise<{ message: string }> => {
    const response = await apiClient.delete(
      `/ai/expert/api/agent-system/agents/${agentId}/unassign_tool/?tool_name=${encodeURIComponent(toolName)}`
    );
    return response.data;
  },

  // Sub-Agent (Agent-as-Tool) Management
  // =====================================

  // Get available and assigned sub-agents
  getSubAgents: async (agentId: number): Promise<AvailableSubAgents> => {
    const response = await apiClient.get(`/ai/expert/api/agent-system/agents/${agentId}/sub_agents/`);
    return response.data;
  },

  // Assign sub-agent to agent
  assignSubAgent: async (agentId: number, data: SubAgentAssignmentRequest): Promise<SubAgentAssignment> => {
    const response = await apiClient.post(
      `/ai/expert/api/agent-system/agents/${agentId}/assign_sub_agent/`,
      data
    );
    return response.data;
  },

  // Update sub-agent assignment
  updateSubAgentAssignment: async (
    agentId: number,
    assignmentId: number,
    data: SubAgentAssignmentUpdate
  ): Promise<SubAgentAssignment> => {
    const response = await apiClient.patch(
      `/ai/expert/api/agent-system/agents/${agentId}/sub_agents/${assignmentId}/`,
      data
    );
    return response.data;
  },

  // Unassign sub-agent from agent
  unassignSubAgent: async (agentId: number, subAgentId: number): Promise<void> => {
    await apiClient.delete(
      `/ai/expert/api/agent-system/agents/${agentId}/unassign_sub_agent/`,
      { params: { sub_agent_id: subAgentId } }
    );
  },

  // Export / Import / Clone
  // =====================================

  // Export agent (+ sub-agent graph) as a JSON payload.
  exportAgent: async (
    agentId: number,
    options: { maskApiKeys?: boolean; includeSubAgents?: boolean } = {}
  ): Promise<Record<string, unknown>> => {
    const { maskApiKeys = true, includeSubAgents = true } = options;
    const response = await apiClient.get(
      `/ai/expert/api/agent-system/agents/${agentId}/export/`,
      {
        params: {
          mask_api_keys: maskApiKeys,
          include_sub_agents: includeSubAgents,
        },
      }
    );
    return response.data as Record<string, unknown>;
  },

  // Import agent export payload.
  importAgent: async (
    payload: Record<string, unknown>,
    options: {
      apiKeyMapping?: Record<string, string>;
      nameSuffix?: string;
      forceNew?: boolean;
    } = {}
  ): Promise<AgentDetail> => {
    const body = {
      data: payload,
      api_key_mapping: options.apiKeyMapping,
      name_suffix: options.nameSuffix,
      force_new: options.forceNew,
    };
    const response = await apiClient.post(
      `/ai/expert/api/agent-system/agents/import/`,
      body
    );
    return response.data as AgentDetail;
  },

  // Clone an agent (and its sub-agents) for the current user.
  cloneAgent: async (
    agentId: number,
    options: { name?: string } = {}
  ): Promise<AgentDetail> => {
    const body = options.name ? { name: options.name } : {};
    const response = await apiClient.post(
      `/ai/expert/api/agent-system/agents/${agentId}/clone/`,
      body
    );
    return response.data as AgentDetail;
  },

  // Execution Management
  // =======================

  // Execute agent (non-streaming)
  // Backend-owned operating profile for the auto-trading creation proof:
  // the single source the UI reads for mode, fixed settings and symbols.
  getOperatingProfile: async (): Promise<import('../types/agent').BuilderOperatingProfile> => {
    const response = await apiClient.get('/ai/expert/api/agent-system/builder/operating-profile/');
    return response.data;
  },

  execute: async (agentId: number, request: AgentExecuteRequest): Promise<AgentExecution> => {
    const response = await apiClient.post(
      `/ai/expert/api/agent-system/agents/${agentId}/execute/`,
      { ...request, stream: false },
      { timeout: EXECUTION_TIMEOUT } // Use extended timeout for agent execution
    );
    return response.data;
  },

  // Get agent execution history
  getExecutions: async (
    agentId: number,
    page: number = 1,
    pageSize: number = 20
  ): Promise<PaginatedExecutionResponse> => {
    const response = await apiClient.get(
      `/ai/expert/api/agent-system/agents/${agentId}/executions/`,
      { params: { page, page_size: pageSize } }
    );
    return response.data;
  },

  // Get single execution details
  getExecution: async (executionId: string): Promise<AgentExecution> => {
    const response = await apiClient.get(`/ai/expert/api/agent-system/executions/${executionId}/`);
    return response.data;
  },

  getExecutionSummary: async (executionId: string): Promise<AgentExecutionSummary> => {
    const response = await apiClient.get(`/ai/expert/api/agent-system/executions/${executionId}/summary/`);
    return response.data;
  },

  // Stop execution
  stopExecution: async (executionId: string): Promise<void> => {
    await apiClient.post(`/ai/expert/api/agent-system/executions/${executionId}/stop/`);
  },

  // Confirm or deny a dangerous tool execution
  confirmTool: async (executionId: string, toolName: string, confirmed: boolean): Promise<void> => {
    await apiClient.post(`/ai/expert/api/agent-system/executions/${executionId}/confirm_tool/`, {
      tool_name: toolName,
      confirmed,
    });
  },

  // Get execution messages
  getExecutionMessages: async (executionId: string): Promise<ConversationMessage[]> => {
    const response = await apiClient.get(
      `/ai/expert/api/agent-system/executions/${executionId}/messages/`
    );
    return response.data;
  },

  // Memory Management
  // =======================

  // Get agent memories
  getMemories: async (
    agentId: number,
    page: number = 1,
    pageSize: number = 20
  ): Promise<PaginatedMemoryResponse> => {
    const response = await apiClient.get(
      `/ai/expert/api/agent-system/agents/${agentId}/memories/`,
      { params: { page, page_size: pageSize } }
    );
    return response.data;
  },

  searchMemories: async (filters: MemorySearchFilters): Promise<PaginatedMemoryResponse> => {
    const params: Record<string, any> = {
      agent_id: filters.agent_id,
      limit: filters.limit || 200,
    };

    if (filters.query) params.query = filters.query;
    if (filters.memory_type) params.memory_type = filters.memory_type;
    if (filters.min_importance !== undefined) params.min_importance = filters.min_importance;
    if (filters.include_published !== undefined) {
      params.include_published = filters.include_published;
    }
    if (filters.read_scopes?.length) {
      params.read_scopes = filters.read_scopes.join(',');
    }
    if (filters.semantic) params.semantic = true;

    const response = await apiClient.get('/ai/expert/api/agent-system/memories/search/', {
      params,
    });

    return {
      count: response.data.count,
      next: null,
      previous: null,
      results: response.data.results,
    };
  },

  // Get memory statistics
  getMemoryStats: async (
    agentId: number,
    options?: { include_published?: boolean; read_scopes?: string[] }
  ): Promise<AgentMemoryStats> => {
    const params: Record<string, any> = { agent_id: agentId };
    if (options?.include_published !== undefined) {
      params.include_published = options.include_published;
    }
    if (options?.read_scopes?.length) {
      params.read_scopes = options.read_scopes.join(',');
    }

    const response = await apiClient.get(
      '/ai/expert/api/agent-system/memories/stats/',
      { params }
    );
    return response.data;
  },

  // Create memory
  createMemory: async (data: AgentMemoryCreate): Promise<AgentMemory> => {
    const response = await apiClient.post('/ai/expert/api/agent-system/memories/', data);
    return response.data;
  },

  publishMemory: async (memoryId: number): Promise<AgentMemory> => {
    const response = await apiClient.post(
      `/ai/expert/api/agent-system/memories/${memoryId}/publish/`,
      {}
    );
    return response.data;
  },

  // Update memory
  updateMemory: async (memoryId: number, data: AgentMemoryUpdate): Promise<AgentMemory> => {
    const response = await apiClient.patch(
      `/ai/expert/api/agent-system/memories/${memoryId}/`,
      data
    );
    return response.data;
  },

  // Delete memory
  deleteMemory: async (memoryId: number): Promise<void> => {
    await apiClient.delete(`/ai/expert/api/agent-system/memories/${memoryId}/`);
  },

  // Conversation Session Management
  // =======================

  // List Main Assistant sessions (dedicated endpoint)
  getMainAssistantSessions: async (
    archived?: boolean,
    page: number = 1,
    pageSize: number = 20
  ): Promise<PaginatedSessionResponse> => {
    const params: Record<string, any> = { page, page_size: pageSize };
    if (archived !== undefined) params.archived = archived;
    
    const response = await apiClient.get('/ai/expert/api/agent-system/main-assistant/sessions/', { params });
    return response.data;
  },

  // List conversation sessions
  getSessions: async (
    agentId?: number,
    isActive?: boolean,
    page: number = 1,
    pageSize: number = 20,
    aiSystemId?: number
  ): Promise<PaginatedSessionResponse> => {
    const params: Record<string, any> = { page, page_size: pageSize };
    if (agentId !== undefined) params.agent_id = agentId;
    if (isActive !== undefined) params.is_active = isActive;
    if (aiSystemId !== undefined) params.ai_system_id = aiSystemId;
    
    const response = await apiClient.get('/ai/expert/api/agent-system/sessions/', { params });
    return response.data;
  },

  // Get the latest conversation session with messages
  getLatestSession: async (
    agentId?: number,
    isActive?: boolean,
    aiSystemId?: number
  ): Promise<ConversationSession | null> => {
    const params: Record<string, any> = {};
    if (agentId !== undefined) params.agent_id = agentId;
    if (isActive !== undefined) params.is_active = isActive;
    if (aiSystemId !== undefined) params.ai_system_id = aiSystemId;

    const response = await apiClient.get('/ai/expert/api/agent-system/sessions/latest/', { params });
    return response.data;
  },

  // Get single session with all messages
  getSession: async (sessionId: string): Promise<ConversationSession> => {
    const response = await apiClient.get(`/ai/expert/api/agent-system/sessions/${sessionId}/`);
    return response.data;
  },

  // Create new session
  createSession: async (data: ConversationSessionCreate): Promise<ConversationSession> => {
    const response = await apiClient.post('/ai/expert/api/agent-system/sessions/', data);
    return response.data;
  },

  // Update session
  updateSession: async (
    sessionId: string,
    data: ConversationSessionUpdate
  ): Promise<ConversationSession> => {
    const response = await apiClient.patch(
      `/ai/expert/api/agent-system/sessions/${sessionId}/`,
      data
    );
    return response.data;
  },

  // Delete session
  deleteSession: async (sessionId: string): Promise<void> => {
    await apiClient.delete(`/ai/expert/api/agent-system/sessions/${sessionId}/`);
  },

  // Archive session
  archiveSession: async (sessionId: string): Promise<ConversationSession> => {
    const response = await apiClient.post(
      `/ai/expert/api/agent-system/sessions/${sessionId}/archive/`
    );
    return response.data;
  },

  // Unarchive session
  unarchiveSession: async (sessionId: string): Promise<ConversationSession> => {
    const response = await apiClient.post(
      `/ai/expert/api/agent-system/sessions/${sessionId}/unarchive/`
    );
    return response.data;
  },

  // Auto-generate session title from first message
  generateSessionTitle: async (sessionId: string): Promise<ConversationSession> => {
    const response = await apiClient.post(
      `/ai/expert/api/agent-system/sessions/${sessionId}/generate_title/`
    );
    return response.data;
  },

  // Get all message snapshots for a session
  getSessionSnapshots: async (sessionId: string): Promise<SessionSnapshotsResponse> => {
    const response = await apiClient.get(
      `/ai/expert/api/agent-system/sessions/${sessionId}/snapshots/`
    );
    return response.data;
  },

  // Get snapshot for a specific message
  getMessageSnapshot: async (sessionId: string, messageId: number): Promise<MessageSnapshotResponse> => {
    const response = await apiClient.get(
      `/ai/expert/api/agent-system/sessions/${sessionId}/messages/${messageId}/snapshot/`
    );
    return response.data;
  },

  // Restore AI system from a message snapshot
  restoreFromSnapshot: async (
    sessionId: string,
    messageId: number,
    options?: RestoreSnapshotOptions
  ): Promise<RestoreSnapshotResponse> => {
    const response = await apiClient.post(
      `/ai/expert/api/agent-system/sessions/${sessionId}/messages/${messageId}/restore/`,
      options || {}
    );
    return response.data;
  },

  // Set snapshot mode for a session
  setSessionSnapshotMode: async (
    sessionId: string,
    snapshotMode: 'NONE' | 'FULL' | 'LIGHTWEIGHT' | 'DIFF'
  ): Promise<{ session_id: string; snapshot_mode: string; message: string }> => {
    const response = await apiClient.post(
      `/ai/expert/api/agent-system/sessions/${sessionId}/set_snapshot_mode/`,
      { snapshot_mode: snapshotMode }
    );
    return response.data;
  },

  // File Attachment Management
  // =======================

  // Upload a file attachment
  uploadAttachment: async (
    file: File,
    description?: string,
    attachmentType?: 'FILE' | 'IMAGE' | 'DOCUMENT' | 'CODE' | 'DATA' | 'AUDIO',
  ): Promise<MessageAttachment> => {
    const formData = new FormData();
    formData.append('file', file);
    if (description) {
      formData.append('description', description);
    }
    if (attachmentType) {
      formData.append('attachment_type', attachmentType);
    }
    
    const response = await apiClient.post(
      '/ai/expert/api/agent-system/attachments/',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  },

  // Get attachment details
  getAttachment: async (attachmentId: string): Promise<MessageAttachment> => {
    const response = await apiClient.get(
      `/ai/expert/api/agent-system/attachments/${attachmentId}/`
    );
    return response.data;
  },

  // Delete an attachment
  deleteAttachment: async (attachmentId: string): Promise<void> => {
    await apiClient.delete(`/ai/expert/api/agent-system/attachments/${attachmentId}/`);
  },

  // Get pending (unattached) attachments
  getPendingAttachments: async (): Promise<MessageAttachment[]> => {
    const response = await apiClient.get(
      '/ai/expert/api/agent-system/attachments/pending/'
    );
    return response.data;
  },

  // Attach file to a message
  attachToMessage: async (attachmentId: string, messageId: number): Promise<MessageAttachment> => {
    const response = await apiClient.post(
      `/ai/expert/api/agent-system/attachments/${attachmentId}/attach_to_message/`,
      { message_id: messageId }
    );
    return response.data;
  },

  // Get attachments for a message
  getMessageAttachments: async (messageId: number): Promise<MessageAttachment[]> => {
    const response = await apiClient.get(
      '/ai/expert/api/agent-system/attachments/by_message/',
      { params: { message_id: messageId } }
    );
    return response.data;
  },
};

// =======================
// Agent Schedule API
// =======================

import type {
  AgentSchedule,
  AgentScheduleCreate,
  AgentScheduleUpdate,
  AgentScheduleFilters,
  AgentScheduleStats,
  AgentScheduleTriggerResponse,
  AgentScheduleExecution,
  AgentScheduleExecutionListItem,
  AgentScheduleExecutionFilters,
  AgentScheduleExecutionStats,
  PaginatedAgentScheduleResponse,
  PaginatedAgentScheduleOptionResponse,
  PaginatedAgentScheduleExecutionResponse,
} from '../types/agent';

export const agentScheduleApi = {
  // List all agent schedules
  list: async (filters?: AgentScheduleFilters): Promise<PaginatedAgentScheduleResponse> => {
    const response = await apiClient.get('/ai/expert/api/agent-system/agent-schedules/', {
      params: filters,
    });
    return response.data;
  },

  // List schedule options for filter dropdowns
  listOptions: async (filters?: Pick<AgentScheduleFilters, 'agent_id' | 'ai_system_id'>): Promise<PaginatedAgentScheduleOptionResponse> => {
    const response = await apiClient.get('/ai/expert/api/agent-system/agent-schedules/', {
      params: { ...filters, summary: true, page_size: 100 },
    });
    return response.data;
  },

  // Get single agent schedule
  get: async (id: number): Promise<AgentSchedule> => {
    const response = await apiClient.get(`/ai/expert/api/agent-system/agent-schedules/${id}/`);
    return response.data;
  },

  // Create new agent schedule
  create: async (data: AgentScheduleCreate): Promise<AgentSchedule> => {
    const response = await apiClient.post('/ai/expert/api/agent-system/agent-schedules/', data);
    return response.data;
  },

  // Update agent schedule
  update: async (id: number, data: AgentScheduleUpdate): Promise<AgentSchedule> => {
    const response = await apiClient.patch(`/ai/expert/api/agent-system/agent-schedules/${id}/`, data);
    return response.data;
  },

  // Delete agent schedule
  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/ai/expert/api/agent-system/agent-schedules/${id}/`);
  },

  // Manually trigger a schedule execution
  trigger: async (id: number, ignoreExclusions: boolean = false): Promise<AgentScheduleTriggerResponse> => {
    const response = await apiClient.post(`/ai/expert/api/agent-system/agent-schedules/${id}/trigger/`, {
      ignore_exclusions: ignoreExclusions,
    });
    return response.data;
  },

  // Toggle schedule active status
  toggle: async (id: number): Promise<{ id: number; is_active: boolean; message: string }> => {
    const response = await apiClient.post(`/ai/expert/api/agent-system/agent-schedules/${id}/toggle/`);
    return response.data;
  },

  // Get execution history for a schedule
  getExecutions: async (
    id: number,
    status?: string,
    limit: number = 20
  ): Promise<AgentScheduleExecution[]> => {
    const params: Record<string, any> = { limit };
    if (status) params.status = status;
    const response = await apiClient.get(
      `/ai/expert/api/agent-system/agent-schedules/${id}/executions/`,
      { params }
    );
    return response.data;
  },

  // Get schedule statistics
  getStats: async (): Promise<AgentScheduleStats> => {
    const response = await apiClient.get('/ai/expert/api/agent-system/agent-schedules/stats/');
    return response.data;
  },
};

// =======================
// Agent Schedule Execution API
// =======================

export const scheduleExecutionApi = {
  // List all schedule executions
  list: async (filters?: AgentScheduleExecutionFilters): Promise<PaginatedAgentScheduleExecutionResponse> => {
    const response = await apiClient.get('/ai/expert/api/agent-system/schedule-executions/', {
      params: filters,
    });
    return response.data;
  },

  // Get single execution details
  get: async (id: string): Promise<AgentScheduleExecutionListItem> => {
    const response = await apiClient.get(`/ai/expert/api/agent-system/schedule-executions/${id}/`);
    return response.data;
  },

  // Get full response without truncation
  getFullResponse: async (id: string): Promise<{
    id: string;
    status: string;
    response: string;
    error: string;
    execution_context: Record<string, any>;
  }> => {
    const response = await apiClient.get(`/ai/expert/api/agent-system/schedule-executions/${id}/full_response/`);
    return response.data;
  },

  // Stop a running execution
  stop: async (id: string): Promise<{
    success: boolean;
    message: string;
    execution_id: string;
    agent_execution_id?: string;
    error?: string;
  }> => {
    const response = await apiClient.post(`/ai/expert/api/agent-system/schedule-executions/${id}/stop/`);
    return response.data;
  },

  // Get tool calls for an execution
  getToolCalls: async (id: string): Promise<{
    tool_calls: Array<{
      step_number: number;
      tool_name: string;
      tool_input: Record<string, any>;
      tool_output: Record<string, any>;
      tokens_used: number;
      duration_ms: number;
      created_at: string | null;
    }>;
    count: number;
    agent_execution_id?: string;
    message?: string;
  }> => {
    const response = await apiClient.get(`/ai/expert/api/agent-system/schedule-executions/${id}/tool_calls/`);
    return response.data;
  },

  // Get execution statistics
  getStats: async (
    days: number = 7,
    filters?: Pick<AgentScheduleExecutionFilters, 'schedule_id' | 'agent_id' | 'ai_system_id'>
  ): Promise<AgentScheduleExecutionStats> => {
    const response = await apiClient.get('/ai/expert/api/agent-system/schedule-executions/stats/', {
      params: { days, ...filters },
    });
    return response.data;
  },
};

// =======================
// Backtest Config API
// =======================
export const backtestConfigApi = {
  list: async (filters?: AIBacktestConfigFilters): Promise<PaginatedResponse<AIBacktestConfig>> => {
    const response = await apiClient.get('/ai/expert/api/backtest-configs/', { params: filters });
    return response.data;
  },

  get: async (id: string): Promise<AIBacktestConfig> => {
    const response = await apiClient.get(`/ai/expert/api/backtest-configs/${id}/`);
    return response.data;
  },

  create: async (data: AIBacktestConfigCreate): Promise<AIBacktestConfig> => {
    const response = await apiClient.post('/ai/expert/api/backtest-configs/', data);
    return response.data;
  },

  update: async (id: string, data: Partial<AIBacktestConfigCreate>): Promise<AIBacktestConfig> => {
    const response = await apiClient.patch(`/ai/expert/api/backtest-configs/${id}/`, data);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/ai/expert/api/backtest-configs/${id}/`);
  },
};

// =======================
// Backtest Run API
// =======================
export const backtestRunApi = {
  list: async (filters?: AIBacktestRunFilters): Promise<PaginatedResponse<AIBacktestRun>> => {
    const response = await apiClient.get('/ai/expert/api/backtest-runs/', { params: filters });
    return response.data;
  },

  get: async (id: string): Promise<AIBacktestRunDetail> => {
    const response = await apiClient.get(`/ai/expert/api/backtest-runs/${id}/`);
    return response.data;
  },

  start: async (configId: string): Promise<AIBacktestRun> => {
    const response = await apiClient.post('/ai/expert/api/backtest-runs/start/', { 
      config_id: configId 
    });
    return response.data;
  },

  stop: async (id: string): Promise<AIBacktestRun> => {
    const response = await apiClient.post(`/ai/expert/api/backtest-runs/${id}/stop/`);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/ai/expert/api/backtest-runs/${id}/`);
  },
};

// =======================
// Backtest Trade API
// =======================
export const backtestTradeApi = {
  list: async (runId: string): Promise<PaginatedResponse<AIBacktestTrade>> => {
    const response = await apiClient.get('/ai/expert/api/backtest-trades/', { 
      params: { backtest_run: runId } 
    });
    return response.data;
  },
};

// =======================
// Shared Input API
// =======================
import type { 
  SharedInput, 
  SharedInputCreate, 
  SharedInputListItem, 
  SharedInputUsage, 
  SharedInputType 
} from '../types';

export const sharedInputApi = {
  list: async (systemId: number): Promise<PaginatedResponse<SharedInputListItem>> => {
    const response = await apiClient.get(`/ai/expert/api/systems/${systemId}/shared-inputs/`);
    return response.data;
  },

  get: async (systemId: number, id: number): Promise<SharedInput> => {
    const response = await apiClient.get(`/ai/expert/api/systems/${systemId}/shared-inputs/${id}/`);
    return response.data;
  },

  create: async (systemId: number, data: SharedInputCreate): Promise<SharedInput> => {
    const response = await apiClient.post(`/ai/expert/api/systems/${systemId}/shared-inputs/`, data);
    return response.data;
  },

  update: async (systemId: number, id: number, data: Partial<SharedInputCreate>): Promise<SharedInput> => {
    const response = await apiClient.patch(`/ai/expert/api/systems/${systemId}/shared-inputs/${id}/`, data);
    return response.data;
  },

  delete: async (systemId: number, id: number): Promise<void> => {
    await apiClient.delete(`/ai/expert/api/systems/${systemId}/shared-inputs/${id}/`);
  },

  restore: async (systemId: number, id: number): Promise<SharedInput> => {
    const response = await apiClient.post(`/ai/expert/api/systems/${systemId}/shared-inputs/${id}/restore/`);
    return response.data;
  },

  getUsage: async (systemId: number, id: number): Promise<SharedInputUsage> => {
    const response = await apiClient.get(`/ai/expert/api/systems/${systemId}/shared-inputs/${id}/usage/`);
    return response.data;
  },

  getAvailableTypes: async (systemId: number): Promise<SharedInputType[]> => {
    const response = await apiClient.get(`/ai/expert/api/systems/${systemId}/shared-inputs/available_types/`);
    return response.data;
  },

  getByType: async (systemId: number, inputType: string): Promise<SharedInputListItem[]> => {
    const response = await apiClient.get(`/ai/expert/api/systems/${systemId}/shared-inputs/by_type/`, {
      params: { input_type: inputType }
    });
    return response.data;
  },

  /**
   * Test an input configuration and return its resolved output.
   * Useful for verifying indicator data, price data, etc. before saving.
   */
  testInput: async (systemId: number, inputType: string, config: Record<string, any>): Promise<{
    success: boolean;
    data?: string;
    data_type?: string;
    data_length?: number;
    truncated?: boolean;
    execution_time_ms: number;
    error?: string;
    error_type?: string;
    traceback?: string;
  }> => {
    const response = await apiClient.post(`/ai/expert/api/systems/${systemId}/shared-inputs/test_input/`, {
      input_type: inputType,
      config
    });
    return response.data;
  },
};

// =======================
// AI System Storage API
// =======================
import type { AISystemStorage, AISystemStorageCreate, AISystemStorageListItem, GlobalStorage, GlobalStorageCreate, GlobalStorageListItem } from '../types';

export const storageApi = {
  list: async (aiSystemId: number, params?: { storage_type?: string; search?: string }): Promise<PaginatedResponse<AISystemStorageListItem>> => {
    const response = await apiClient.get('/ai/expert/api/storage/', {
      params: { ai_system: aiSystemId, ...params }
    });
    return response.data;
  },

  get: async (id: number): Promise<AISystemStorage> => {
    const response = await apiClient.get(`/ai/expert/api/storage/${id}/`);
    return response.data;
  },

  getByName: async (aiSystemId: number, name: string): Promise<AISystemStorage> => {
    const response = await apiClient.get('/ai/expert/api/storage/by_name/', {
      params: { ai_system: aiSystemId, name }
    });
    return response.data;
  },

  getByTags: async (aiSystemId: number, tags: string[], matchAll?: boolean): Promise<AISystemStorageListItem[]> => {
    const response = await apiClient.get('/ai/expert/api/storage/by_tags/', {
      params: { ai_system: aiSystemId, tags: tags.join(','), match_all: matchAll }
    });
    return response.data;
  },

  create: async (data: AISystemStorageCreate): Promise<AISystemStorage> => {
    // Use FormData for file uploads
    if (data.file) {
      const formData = new FormData();
      formData.append('ai_system', String(data.ai_system));
      formData.append('name', data.name);
      formData.append('storage_type', data.storage_type || 'file');
      formData.append('file', data.file);
      if (data.tags) formData.append('tags', JSON.stringify(data.tags));
      if (data.metadata) formData.append('metadata', JSON.stringify(data.metadata));
      
      const response = await apiClient.post('/ai/expert/api/storage/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    }
    
    const response = await apiClient.post('/ai/expert/api/storage/', data);
    return response.data;
  },

  update: async (id: number, data: Partial<AISystemStorageCreate>): Promise<AISystemStorage> => {
    // Use FormData for file uploads
    if (data.file) {
      const formData = new FormData();
      if (data.name) formData.append('name', data.name);
      if (data.storage_type) formData.append('storage_type', data.storage_type);
      formData.append('file', data.file);
      if (data.tags) formData.append('tags', JSON.stringify(data.tags));
      if (data.metadata) formData.append('metadata', JSON.stringify(data.metadata));
      
      const response = await apiClient.patch(`/ai/expert/api/storage/${id}/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    }
    
    const response = await apiClient.patch(`/ai/expert/api/storage/${id}/`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/ai/expert/api/storage/${id}/`);
  },

  getContent: async (id: number): Promise<{ type: string; content?: string; file_url?: string; metadata?: Record<string, any> }> => {
    const response = await apiClient.get(`/ai/expert/api/storage/${id}/content/`);
    return response.data;
  },
};

// =======================
// Global Storage API
// =======================

export const globalStorageApi = {
  list: async (params?: { storage_type?: string; search?: string }): Promise<PaginatedResponse<GlobalStorageListItem>> => {
    const response = await apiClient.get('/ai/expert/api/global-storage/', {
      params: { ...params }
    });
    return response.data;
  },

  get: async (id: number): Promise<GlobalStorage> => {
    const response = await apiClient.get(`/ai/expert/api/global-storage/${id}/`);
    return response.data;
  },

  getByName: async (name: string): Promise<GlobalStorage> => {
    const response = await apiClient.get('/ai/expert/api/global-storage/by_name/', {
      params: { name }
    });
    return response.data;
  },

  getByTags: async (tags: string[], matchAll?: boolean): Promise<GlobalStorageListItem[]> => {
    const response = await apiClient.get('/ai/expert/api/global-storage/by_tags/', {
      params: { tags: tags.join(','), match_all: matchAll }
    });
    return response.data;
  },

  create: async (data: GlobalStorageCreate): Promise<GlobalStorage> => {
    // Use FormData for file uploads
    if (data.file) {
      const formData = new FormData();
      formData.append('name', data.name);
      formData.append('storage_type', data.storage_type || 'file');
      formData.append('file', data.file);
      if (data.tags) formData.append('tags', JSON.stringify(data.tags));
      if (data.metadata) formData.append('metadata', JSON.stringify(data.metadata));
      
      const response = await apiClient.post('/ai/expert/api/global-storage/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    }
    
    const response = await apiClient.post('/ai/expert/api/global-storage/', data);
    return response.data;
  },

  update: async (id: number, data: Partial<GlobalStorageCreate>): Promise<GlobalStorage> => {
    // Use FormData for file uploads
    if (data.file) {
      const formData = new FormData();
      if (data.name) formData.append('name', data.name);
      if (data.storage_type) formData.append('storage_type', data.storage_type);
      formData.append('file', data.file);
      if (data.tags) formData.append('tags', JSON.stringify(data.tags));
      if (data.metadata) formData.append('metadata', JSON.stringify(data.metadata));
      
      const response = await apiClient.patch(`/ai/expert/api/global-storage/${id}/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    }
    
    const response = await apiClient.patch(`/ai/expert/api/global-storage/${id}/`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/ai/expert/api/global-storage/${id}/`);
  },

  getContent: async (id: number): Promise<{ type: string; content?: string; file_url?: string; metadata?: Record<string, any> }> => {
    const response = await apiClient.get(`/ai/expert/api/global-storage/${id}/content/`);
    return response.data;
  },
};

// ------- Service Instructions API ------- //
import type {
  ServiceInstruction,
  ServiceInstructionCreate,
  ServiceInstructionListItem,
  ServiceInstructionCategory,
} from '../types';

export const serviceInstructionsApi = {
  list: async (params?: {
    category?: ServiceInstructionCategory;
    is_active?: boolean;
    search?: string;
    ordering?: string;
  }): Promise<ServiceInstructionListItem[]> => {
    const response = await apiClient.get('/service-instructions/', { params });
    // DRF router may return paginated or flat list
    return Array.isArray(response.data) ? response.data : response.data.results ?? response.data;
  },

  get: async (slug: string): Promise<ServiceInstruction> => {
    const response = await apiClient.get(`/service-instructions/${slug}/`);
    return response.data;
  },

  getByKey: async (serviceKey: string): Promise<ServiceInstruction> => {
    const response = await apiClient.get(`/service-instructions/by-key/${serviceKey}/`);
    return response.data;
  },

  getByCategory: async (category: ServiceInstructionCategory): Promise<ServiceInstructionListItem[]> => {
    const response = await apiClient.get(`/service-instructions/by-category/${category}/`);
    return response.data;
  },

  search: async (q: string): Promise<ServiceInstructionListItem[]> => {
    const response = await apiClient.get('/service-instructions/search/', { params: { q } });
    return response.data;
  },

  create: async (data: ServiceInstructionCreate): Promise<ServiceInstruction> => {
    const response = await apiClient.post('/service-instructions/', data);
    return response.data;
  },

  update: async (slug: string, data: Partial<ServiceInstructionCreate>): Promise<ServiceInstruction> => {
    const response = await apiClient.patch(`/service-instructions/${slug}/`, data);
    return response.data;
  },

  delete: async (slug: string): Promise<void> => {
    await apiClient.delete(`/service-instructions/${slug}/`);
  },
};

// ------- ML Compute Infrastructure API ------- //

export const computeSpecsApi = {
  list: async (filters?: { compute_mode?: string; executor?: string }): Promise<ComputeSpec[]> => {
    const response = await apiClient.get('/model-lab/compute-specs/', { params: filters });
    return Array.isArray(response.data) ? response.data : response.data.results ?? response.data;
  },

  get: async (id: number): Promise<ComputeSpec> => {
    const response = await apiClient.get(`/model-lab/compute-specs/${id}/`);
    return response.data;
  },

  create: async (data: ComputeSpecCreate): Promise<ComputeSpec> => {
    const response = await apiClient.post('/model-lab/compute-specs/', data);
    return response.data;
  },

  update: async (id: number, data: Partial<ComputeSpecCreate>): Promise<ComputeSpec> => {
    const response = await apiClient.patch(`/model-lab/compute-specs/${id}/`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/model-lab/compute-specs/${id}/`);
  },

  suggest: async (jobType: string, jobId: number): Promise<ComputeSuggestion> => {
    const response = await apiClient.get('/model-lab/compute-specs/suggest/', {
      params: { job_type: jobType, job_id: jobId },
    });
    return response.data;
  },
};

export const computeVMsApi = {
  list: async (filters?: { status?: string; purpose?: string }): Promise<ComputeVM[]> => {
    const response = await apiClient.get('/model-lab/compute-vms/', { params: filters });
    return Array.isArray(response.data) ? response.data : response.data.results ?? response.data;
  },

  get: async (id: number): Promise<ComputeVM> => {
    const response = await apiClient.get(`/model-lab/compute-vms/${id}/`);
    return response.data;
  },
};

// Export configured axios instance for custom requests
export default apiClient;
